// Landing page interactions: scroll reveals, headline word flip,
// stat count-ups, subject ticker, and the looping generator demo.
(function () {
    'use strict';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sleep = ms => new Promise(r => setTimeout(r, ms));

    /* ── Marketplace showcase ───────────────────────────── */
    // Real published lessons, scrolling past. Fetched rather than baked in, so
    // the strip is never a mock-up of a Marketplace that does not exist yet –
    // and the whole section stays hidden if nothing has been published.
    const marketRail = document.getElementById('lp-market-rail');
    const marketTrack = document.getElementById('lp-market-track');

    function marketCard(row) {
        const a = document.createElement('a');
        a.className = 'lp-market-card';
        a.href = '/marketplace/' + row.id;
        const facets = [row.subject, row.level, row.exam_board].filter(Boolean).join(' · ');
        const uses = row.clone_count
            ? 'Used by ' + row.clone_count + ' teacher' + (row.clone_count === 1 ? '' : 's')
            : 'New';
        a.innerHTML =
            '<div class="lp-market-thumb">' +
                '<div class="lp-market-slide" data-theme="' + (row.theme || 'classic') + '"></div>' +
                (row.slide_count ? '<span class="lp-market-count">' + row.slide_count + ' slides</span>' : '') +
            '</div>' +
            '<div class="lp-market-body">' +
                '<h3></h3>' +
                '<p class="lp-market-meta"></p>' +
                '<p class="lp-market-uses"></p>' +
            '</div>';
        // textContent for anything author-supplied: this is the one place on
        // the landing page that renders other people's words.
        a.querySelector('h3').textContent = row.title || 'Untitled';
        a.querySelector('.lp-market-meta').textContent =
            facets || (row.author_name ? 'By ' + row.author_name : 'By a teacher');
        a.querySelector('.lp-market-uses').textContent = uses;
        // The thumbnail is sanitised slide HTML from the listing snapshot, the
        // same markup the Marketplace itself renders.
        const slideEl = a.querySelector('.lp-market-slide');
        if (row.thumbnail_html) slideEl.innerHTML = row.thumbnail_html;
        return a;
    }

    // Slides are authored at a fixed 960x540 and positioned absolutely, so the
    // thumbnail box has no height until the scale is applied.
    function scaleMarketSlides() {
        const slides = marketTrack ? marketTrack.querySelectorAll('.lp-market-slide') : [];
        slides.forEach(el => {
            const box = el.parentElement;
            const w = box.clientWidth;
            if (!w) return;
            el.style.transform = 'scale(' + (w / 960) + ')';
        });
    }

    // Seamless marquee.
    //
    // Translating the track by -50% only loops cleanly when the track is
    // exactly two identical halves – which it never is, because flex `gap`
    // puts one fewer gap between N cards than the pitch of one copy, so the
    // seam walks a little further every cycle until the cards run out and
    // leave blank rail behind them.
    //
    // Instead: measure one copy's pitch (cards + one gap each, including a
    // trailing gap so the seam spacing matches every other), then shift by
    // exactly that. Enough copies are appended to cover the rail plus one
    // full pitch, so there is always content where the shift lands, however
    // few listings there are or how wide the screen is.
    const MARQUEE_PX_PER_SEC = 55;   // slow enough to read a card as it passes
    let marketItems = [];

    function buildMarquee() {
        if (!marketTrack || !marketItems.length) return;
        // Everything below is a measurement, and every measurement inside a
        // display:none subtree is zero. The landing view is hidden until the
        // auth check finishes and is hidden again between sessions, so building
        // here would produce a pitch of one gap, a single copy, and unscaled
        // 960px slides. The observer below re-runs this once the rail has a
        // real width.
        if (!marketRail || !marketRail.clientWidth) return;

        marketTrack.classList.remove('is-looping');
        marketTrack.style.removeProperty('--marquee-shift');
        marketTrack.innerHTML = '';

        marketItems.forEach(row => marketTrack.appendChild(marketCard(row)));

        if (reduceMotion || marketItems.length < 2) {
            requestAnimationFrame(scaleMarketSlides);
            return;
        }

        const gap = parseFloat(getComputedStyle(marketTrack).columnGap) || 0;
        const pitch = marketTrack.scrollWidth + gap;
        // A pitch no bigger than one gap means the cards measured as zero-width
        // – laid out but not yet painted. Bail rather than animate a track that
        // is 18px wide.
        if (pitch <= gap + 1) return;
        const railWidth = marketRail.clientWidth;
        // One copy to shift through, plus enough to keep the rail full.
        const copies = Math.ceil(railWidth / pitch) + 1;

        for (let c = 0; c < copies; c++) {
            marketItems.forEach(row => {
                const clone = marketCard(row);
                // A screen reader should hear the list once, not once per copy.
                clone.setAttribute('aria-hidden', 'true');
                clone.tabIndex = -1;
                marketTrack.appendChild(clone);
            });
        }

        marketTrack.style.setProperty('--marquee-shift', pitch + 'px');
        // Constant speed regardless of how many listings there are, so adding
        // one does not make the whole strip move faster.
        marketTrack.style.animationDuration = (pitch / MARQUEE_PX_PER_SEC) + 's';
        marketTrack.classList.add('is-looping');
        requestAnimationFrame(scaleMarketSlides);
    }

    if (marketTrack) {
        fetch('/marketplace/top.json?limit=10')
            .then(r => (r.ok ? r.json() : null))
            .then(data => {
                marketItems = (data && data.items) || [];
                if (!marketItems.length) return;
                document.getElementById('lp-market').hidden = false;
                buildMarquee();
            })
            .catch(() => { /* the strip is a bonus; the page is fine without it */ });

        // A ResizeObserver rather than a window resize listener: this has to
        // catch the rail going from hidden to visible as well as the window
        // changing size. Signing out calls show(landingView) without reloading,
        // so a strip built while the page was hidden would otherwise stay
        // broken – unscaled slides and a track one gap wide – until a manual
        // window resize.
        let resizeTimer = null;
        let lastRailWidth = 0;
        if ('ResizeObserver' in window) {
            new ResizeObserver(() => {
                const w = marketRail.clientWidth;
                if (w === lastRailWidth) return;
                lastRailWidth = w;
                if (!w) return;
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(buildMarquee, 120);
            }).observe(marketRail);
        } else {
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(buildMarquee, 200);
            });
        }
    }

    /* ── Scroll reveals (transitions.dev texts-reveal) ──── */
    // Reveals are grouped by their parent so a block of copy rises with
    // rhythm instead of every line landing at once. The stagger offset is
    // capped so the last line of a long group never feels late.
    const revealEls = document.querySelectorAll('.lp-reveal');
    const staggerMs = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--stagger-stagger')
    ) || 40;
    const MAX_STAGGERED = 7; // 7 × 40ms keeps the whole group under ~300ms
    const groups = new Set();
    revealEls.forEach(el => {
        const parent = el.parentElement;
        if (!parent) return;
        parent.classList.add('t-stagger');
        groups.add(parent);
        const index = [...parent.children].filter(c => c.classList.contains('lp-reveal')).indexOf(el);
        el.classList.add('t-stagger-line');
        el.style.transitionDelay = `${Math.min(index, MAX_STAGGERED) * staggerMs}ms`;
    });
    if ('IntersectionObserver' in window && !reduceMotion) {
        const io = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('is-shown');
                    io.unobserve(e.target);
                }
            });
        }, { threshold: 0.15 });
        groups.forEach(el => io.observe(el));
    } else {
        groups.forEach(el => el.classList.add('is-shown'));
    }

    /* ── Headline word flip ─────────────────────────────── */
    const flip = document.getElementById('lp-flip');
    if (flip && !reduceMotion) {
        const words = Array.from(flip.querySelectorAll('.lp-flip-word'));
        let i = 0;
        setInterval(() => {
            words[i].classList.remove('is-in');
            i = (i + 1) % words.length;
            words[i].classList.add('is-in');
        }, 2400);
    }

    /* ── Marking vignette ────────────────────────────────
       Every pass marks a different answer: fresh mark, fresh phrases picked
       out, and a colour mix that follows the mark – a strong one is mostly
       green credit, a weak one collects amber gaps and red off-target. The
       markup in index.html is the same thing drawn once, for no-JS. */
    const markPaper = document.querySelector('.lp-vig-mark');
    const stampScore = document.querySelector('.lp-vmscore');
    if (markPaper && stampScore) {
        const MIN_SCORE = 3, MAX_SCORE = 9;
        // Widths the lines aim for. The last two stay short so nothing runs
        // under the mark stamped on the bottom-right corner.
        const LINE_WIDTHS = [92, 90, 90, 70, 46];

        const randInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));

        // Odds of [credit, gap, off-target] for each mark. Set out per mark
        // rather than scaled off it, so a top answer stays clearly green: at
        // 9/10 there is nothing off-target left to find, and red only really
        // shows up once the mark drops into the sixes.
        const KINDS = ['hl-credit', 'hl-gap', 'hl-off'];
        const KIND_ODDS = {
            3: [1, 3, 6],
            4: [2, 4, 5],
            5: [3, 5, 4],
            6: [4, 5, 3],
            7: [8, 4, 0],
            8: [11, 3, 0],
            9: [14, 2, 0],
        };

        function pickKind(score) {
            const odds = KIND_ODDS[score] || KIND_ODDS[7];
            let roll = Math.random() * odds.reduce((a, b) => a + b, 0);
            for (let i = 0; i < KINDS.length; i++) {
                if ((roll -= odds[i]) < 0) return KINDS[i];
            }
            return KINDS[0];
        }

        // Even the weakest answer here is half marks, so it earned credit
        // somewhere: never let a pass come back with nothing green on it.
        function pickKinds(score, count) {
            const kinds = Array.from({ length: count }, () => pickKind(score));
            if (kinds.indexOf('hl-credit') === -1) {
                kinds[Math.floor(Math.random() * kinds.length)] = 'hl-credit';
            }
            return kinds;
        }

        // Break a line into word-ish runs that add up to about `total`.
        function splitLine(total, parts) {
            const weights = Array.from({ length: parts }, () => 1 + Math.random());
            const sum = weights.reduce((a, b) => a + b, 0);
            return weights.map(w => Math.round(total * w / sum));
        }

        // n distinct line indexes, back in top-to-bottom order.
        function sampleLines(count) {
            const pool = [0, 1, 2, 3];
            const picked = [];
            while (picked.length < count && pool.length) {
                picked.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
            }
            return picked.sort((a, b) => a - b);
        }

        function redraw() {
            const score = randInt(MIN_SCORE, MAX_SCORE);
            stampScore.textContent = String(score);

            const highlighted = sampleLines(randInt(2, 3));
            const kinds = pickKinds(score, highlighted.length);
            const frag = document.createDocumentFragment();
            let position = 0;

            LINE_WIDTHS.forEach((width, i) => {
                const line = document.createElement('span');
                line.className = 'lp-vmline';
                const segments = splitLine(width + randInt(-3, 3), i === 4 ? 1 : randInt(2, 3));
                // The widest run carries the highlight, so it always reads.
                const mark = highlighted.includes(i)
                    ? segments.indexOf(Math.max.apply(null, segments))
                    : -1;

                segments.forEach((segWidth, j) => {
                    const seg = document.createElement('u');
                    seg.style.width = segWidth + '%';
                    if (j === mark) {
                        seg.className = 'lp-hl ' + kinds[position] + ' hl-' + (position + 1);
                        position += 1;
                    }
                    line.appendChild(seg);
                });
                frag.appendChild(line);
            });

            markPaper.querySelectorAll('.lp-vmline').forEach(line => line.remove());
            markPaper.insertBefore(frag, markPaper.firstChild);
        }

        redraw();
        // Fires as the loop wraps, while everything is faded out – so the
        // answer is swapped unseen, and the new highlight animations start
        // in step with the stamp rather than drifting out of phase.
        if (!reduceMotion) {
            stampScore.closest('.lp-vmstamp')
                ?.addEventListener('animationiteration', redraw);
        }
    }

    /* ── SEND band: the dyslexia sample's typeface ───────
       The band is a static diagram – three sheets, each set in the layout it
       names – so there is nothing here to drive. The only job is the font.

       OpenDyslexic is ~300KB of base64 and one small sample needs it, so it is
       not in the landing page's <head>. Fetched the first time the band is on
       screen: late enough not to sit in front of the hero, early enough to be
       there before anyone reads it. Without it the sample falls back to
       Verdana, which is a weaker demo but not a broken one. */
    const senSection = document.getElementById('lp-sen');
    if (senSection) {
        let senFontLoaded = false;
        function loadDyslexicFont() {
            if (senFontLoaded) return;
            senFontLoaded = true;
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = '/css/a11y-fonts.css';
            document.head.appendChild(link);
        }

        if ('IntersectionObserver' in window) {
            const senIO = new IntersectionObserver(entries => {
                entries.forEach(e => {
                    if (!e.isIntersecting) return;
                    loadDyslexicFont();
                    senIO.unobserve(e.target);
                });
            }, { threshold: 0.25 });
            senIO.observe(senSection);
        } else {
            loadDyslexicFont();
        }
    }

    /* ── Stat count-ups ─────────────────────────────────── */
    const statEls = document.querySelectorAll('.lp-stat-num[data-count]');
    function countUp(el) {
        const target = parseInt(el.dataset.count, 10) || 0;
        const suffix = el.dataset.suffix || '';
        if (reduceMotion || target === 0) {
            el.textContent = target + suffix;
            return;
        }
        const dur = 1000;
        const t0 = performance.now();
        (function tick(t) {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            el.textContent = Math.round(target * eased) + suffix;
            if (p < 1) requestAnimationFrame(tick);
        })(t0);
    }
    if ('IntersectionObserver' in window) {
        const statIO = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    countUp(e.target);
                    statIO.unobserve(e.target);
                }
            });
        }, { threshold: 0.6 });
        statEls.forEach(el => statIO.observe(el));
    } else {
        statEls.forEach(countUp);
    }

    /* ── Specification coverage ─────────────────────────── */
    // Built from /api/spec-catalogue, the same index the composer checks
    // against – so this can't claim a board we don't hold, or miss one we just
    // added. Shaped as a finder because the visitor's question is "do you have
    // mine", and a list of 443 makes them do the searching by eye.
    const covSection = document.getElementById('lp-coverage');
    const covInput   = document.getElementById('lp-cov-input');
    const covResults = document.getElementById('lp-cov-results');
    const covFoot    = document.getElementById('lp-cov-foot');
    const COV_MAX_ROWS = 6;      // beyond this it stops being an answer and becomes a list again

    let covIndex = [];           // [{ subject, key, levels: [{ level, boards }] }]
    let covBoards = [];
    let covNC = null;            // the National Curriculum group, which is not an exam board
    let covTotals = {};

    const covEl = (tag, cls, text) => {
        const el = document.createElement(tag);
        if (cls) el.className = cls;
        if (text != null) el.textContent = text;
        return el;
    };

    // Each board keeps one colour everywhere it appears – its tile's badge
    // and its chips in the results – so "which of these is mine" is answered
    // by colour before it is read. Assigned in tile order, biggest board first.
    const COV_COLOURS = ['cc-blue', 'cc-green', 'cc-amber', 'cc-purple', 'cc-sky', 'cc-red'];
    function covColour(board) {
        const i = covBoards.findIndex(b => b.board === board);
        return i < 0 ? 'cc-none' : COV_COLOURS[i % COV_COLOURS.length];
    }
    // "AQA", "OCR", "IB" as they are; longer names by their first two letters.
    const covMono = name => (name.length <= 4 ? name : name.slice(0, 2));

    // The tile counts tick up the first time the wall is on screen.
    let covCounted = false;
    function covCountUp(wall) {
        const nums = wall.querySelectorAll('.lp-cov-count em');
        if (covCounted || reduceMotion || !('IntersectionObserver' in window)) return;
        nums.forEach(el => { el.textContent = '0'; });
        const io = new IntersectionObserver(entries => {
            if (!entries.some(e => e.isIntersecting)) return;
            io.disconnect();
            covCounted = true;
            const t0 = performance.now();
            (function tick(t) {
                const p = Math.min(1, (t - t0) / 1100);
                const eased = 1 - Math.pow(1 - p, 3);
                nums.forEach(el => { el.textContent = Math.round(el.dataset.to * eased); });
                if (p < 1) requestAnimationFrame(tick);
            })(t0);
        }, { threshold: 0.4 });
        io.observe(wall);
    }

    // The empty box types its own examples, taken from the catalogue so it
    // can only ever suggest something we hold. Stops for good the moment
    // anybody focuses it.
    function covTypeExamples() {
        if (reduceMotion || !covInput) return;
        const examples = [];
        covIndex.forEach(entry => entry.levels.forEach(l => {
            if (covNC && l.level === covNC.level) return;
            l.boards.forEach(b => examples.push(b + ' ' + l.level + ' ' + entry.subject));
        }));
        if (!examples.length) return;
        for (let i = examples.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [examples[i], examples[j]] = [examples[j], examples[i]];
        }
        const base = covInput.placeholder;
        let stopped = false, k = 0, timer = 0;
        const stop = () => {
            stopped = true;
            clearTimeout(timer);
            covInput.placeholder = base;
        };
        covInput.addEventListener('focus', stop, { once: true });
        function type(word, n) {
            if (stopped) return;
            covInput.placeholder = 'Try \u201c' + word.slice(0, n) + (n < word.length ? '|' : '\u201d');
            if (n < word.length) timer = setTimeout(() => type(word, n + 1), 55 + Math.random() * 60);
            else timer = setTimeout(() => erase(word, n), 1700);
        }
        function erase(word, n) {
            if (stopped) return;
            covInput.placeholder = 'Try \u201c' + word.slice(0, n) + '|';
            if (n > 0) timer = setTimeout(() => erase(word, n - 1), 22);
            else timer = setTimeout(() => type(examples[++k % examples.length], 1), 300);
        }
        timer = setTimeout(() => type(examples[0], 1), 900);
    }

    // Subject → its levels → the boards holding it, which is the shape of the
    // answer: "Biology — GCSE: AQA, Edexcel, OCR…". The API is keyed the other
    // way round because that is the shape of the data.
    function buildCovIndex(data) {
        const bySubject = new Map();
        (data.levels || []).forEach(group => {
            (group.subjects || []).forEach(entry => {
                if (!bySubject.has(entry.subject)) bySubject.set(entry.subject, []);
                bySubject.get(entry.subject).push({ level: group.level, boards: entry.boards });
            });
        });
        return [...bySubject.entries()]
            .map(([subject, levels]) => ({ subject, key: subject.toLowerCase(), levels }))
            .sort((a, b) => a.subject.localeCompare(b.subject));
    }

    // The row shows what is left after the query's own filters: naming a
    // board drops every other board's chips (they would all say the same
    // word), and naming a level drops the other levels. Nothing named shows
    // everything, which is the case that matters most.
    function covRow(entry, f) {
        const row = covEl('div', 'lp-cov-row');
        row.appendChild(covEl('span', 'lp-cov-row-subject', entry.subject));
        const levels = covEl('span', 'lp-cov-row-levels');
        covLevelsOf(entry, f).forEach(l => {
            const group = covEl('span', 'lp-cov-lvl');
            // Naming the level in the query already answers this for every
            // row; repeating it down the column says nothing.
            if (!f.level) group.appendChild(covEl('b', null, l.level));
            if (!f.board) l.boards.forEach(b => group.appendChild(covEl('i', covColour(b), b)));
            levels.appendChild(group);
        });
        row.appendChild(levels);
        return row;
    }

    /** The entry's levels that survive the query's board/level filters. */
    function covLevelsOf(entry, f) {
        return entry.levels.filter(l =>
            (!f.board || l.boards.includes(f.board)) &&
            (!f.level || l.level === f.level));
    }

    // "OCR biology", "aqa gcse chemistry", "biology ocr" – a board and a level
    // are facets, whatever order they arrive in, and what is left over is the
    // subject. Parsed rather than matched as one string: "ocr biology" matches
    // no subject name, and answering "not that one - yet" to a spec we hold is
    // the one answer this section must never give.
    function covParse(query) {
        const ncLevel = covNC ? covNC.level : null;
        // Key stages are chips in the same position boards are, so they are
        // filtered the same way: "ks2 history" has to work as well as
        // "ocr history".
        const boards = [...new Set(covIndex.flatMap(e => e.levels.flatMap(l => l.boards)))];
        const levels = [...new Set(covIndex.flatMap(e => e.levels.map(l => l.level)))];
        // "A-Level", "a level" and "alevel" are the same word to a teacher.
        const flat = str => str.toLowerCase().replace(/[^a-z0-9]/g, '');
        const words = String(query).trim().toLowerCase().split(/\s+/).filter(Boolean);

        let board = null;
        let level = null;
        const terms = [];
        // Two-word levels ("a level") are joined back up before anything else
        // gets a look at them.
        for (let i = 0; i < words.length; i += 1) {
            const pair = i + 1 < words.length ? flat(words[i] + words[i + 1]) : null;
            const pairLevel = pair && levels.find(l => flat(l) === pair);
            if (pairLevel) { level = pairLevel; i += 1; continue; }

            const word = words[i];
            const asBoard = boards.find(b => b.toLowerCase() === word)
                || (word.length >= 3 ? boards.find(b => b.toLowerCase().startsWith(word)) : null);
            if (asBoard && !board) { board = asBoard; continue; }
            // "National Curriculum" is what that group is called everywhere
            // except in this index, and it is two words: both are consumed,
            // or the second one is left behind as a subject nobody teaches.
            if (ncLevel && /^(national|curriculum|dfe)/.test(word)) {
                if (!level) level = ncLevel;
                continue;
            }
            // Prefix, so "primary" reaches "Primary & KS3" – nobody types the
            // whole of that.
            const asLevel = levels.find(l => flat(l) === flat(word))
                || (word.length >= 3 ? levels.find(l => flat(l).startsWith(flat(word))) : null);
            if (asLevel && !level) { level = asLevel; continue; }
            terms.push(word);
        }
        // What teachers type vs what the boards call it. Only the handful that
        // are near-universal in a UK staffroom – a synonym list is a slippery
        // slope, and "no match" for these three would be a lie about coverage.
        const ALIASES = { maths: 'mathematics', math: 'mathematics', rs: 'religious', re: 'religious', ict: 'computer' };
        const mapped = terms.map(t => ALIASES[t] || t);
        return { board, level, terms: mapped, text: mapped.join(' ') };
    }

    // Nothing typed yet: the boards themselves, biggest first. It answers the
    // other half of the question ("which boards?") without making anyone
    // search for it, and it gives the box something to be when it is empty.
    // Emptying the panel and forgetting the expanded state are the same
    // operation: the scroll cap and its bottom fade belong to a list that is
    // no longer there, and left behind they hang under the board tiles.
    function covClear() {
        covResults.innerHTML = '';
        covResults.classList.remove('is-scrolling');
    }

    function covDefault() {
        covClear();
        const wall = covEl('div', 'lp-cov-wall');
        // The tiles are a shortcut into the same search, not decoration.
        const tile = (cls, title, count, unit, sub, query, mono) => {
            const el = covEl('button', cls);
            el.type = 'button';
            el.appendChild(covEl('i', 'lp-cov-badge', mono));
            const text = covEl('span', 'lp-cov-tile-text');
            text.appendChild(covEl('b', null, title));
            text.appendChild(covEl('small', null, sub));
            el.appendChild(text);
            const n = covEl('span', 'lp-cov-count');
            const num = covEl('em', null, String(count));
            num.dataset.to = count;
            n.appendChild(num);
            n.appendChild(covEl('small', null, unit));
            el.appendChild(n);
            el.addEventListener('click', () => {
                covInput.value = query;
                covRender(query);
                covInput.focus();
            });
            wall.appendChild(el);
            return el;
        };

        covBoards.forEach(b => tile(
            'lp-cov-tile ' + covColour(b.board), b.board, b.specifications, 'specs',
            b.levels.join(' · '), b.board, covMono(b.board)));

        // Primary is a different customer, not a smaller one – as a clause in
        // the footnote it read as an afterthought, so it gets a tile of its own
        // across the bottom of the wall.
        if (covNC) {
            tile('lp-cov-tile lp-cov-tile-nc',
                'National Curriculum',
                covNC.subjects.length, 'subjects',
                covNC.stages.join(' · '),
                'Primary', 'NC');
        }

        covResults.appendChild(wall);
        covCountUp(wall);
        covFoot.innerHTML = '<b>' + covTotals.subjects + '</b> subjects, every one held as the board published it. '
            + 'Type yours to check.';
    }

    function covRender(query) {
        const q = String(query || '').trim().toLowerCase();
        if (!q) return covDefault();

        const f = covParse(q);
        // A board or level on its own is a browse ("show me OCR"), so every
        // subject it holds counts. With words left over it is a search, and
        // those words have to be in the subject name.
        const matches = covIndex.filter(entry => {
            if (f.terms.length && !f.terms.every(t => entry.key.includes(t))) return false;
            return covLevelsOf(entry, f).length > 0;
        });

        covClear();
        if (!matches.length) {
            const none = covEl('p', 'lp-cov-none');
            none.appendChild(covEl('b', null, 'Not that one – yet.'));
            const ask = covEl('span', null, ' Tell us and we’ll add it, usually within a couple of days: ');
            const mail = covEl('a', null, 'support@blueacorn.org');
            mail.href = 'mailto:support@blueacorn.org?subject=Specification%20request';
            ask.appendChild(mail);
            none.appendChild(ask);
            covResults.appendChild(none);
            covFoot.textContent = '';
            return;
        }

        const list = covEl('div', 'lp-cov-rows');
        matches.slice(0, COV_MAX_ROWS).forEach(entry => list.appendChild(covRow(entry, f)));
        covResults.appendChild(list);

        // "12 subjects on OCR at GCSE", or the plain count when the query
        // named neither.
        const facets = (f.board ? ' on ' + f.board : '') + (f.level ? ' at ' + f.level : '');
        const noun = ' subject' + (matches.length === 1 ? '' : 's');
        const whole = matches.length + noun + (facets || ', all held as the board published them');
        const extra = matches.length - COV_MAX_ROWS;
        covFoot.textContent = '';
        if (extra <= 0) {
            covFoot.textContent = whole;
            return;
        }

        // Six is the right first answer, not the whole answer: someone who
        // clicked a board tile wants that board's list. The rest go in behind
        // a scroll rather than pushing the page down by forty rows.
        const more = covEl('button', 'lp-cov-more');
        more.type = 'button';
        more.textContent = 'Show all ' + matches.length + (facets || ' matches');
        more.addEventListener('click', () => {
            matches.slice(COV_MAX_ROWS).forEach(entry => list.appendChild(covRow(entry, f)));
            covResults.classList.add('is-scrolling');
            covFoot.textContent = whole;
        }, { once: true });
        covFoot.appendChild(more);
    }

    if (covSection && covInput && covResults) {
        fetch('/api/spec-catalogue')
            .then(r => (r.ok ? r.json() : null))
            .then(data => {
                if (!data || !data.levels || !data.levels.length) return;
                covIndex = buildCovIndex(data);
                covBoards = data.boards || [];
                covTotals = data.totals || {};
                const nc = data.levels.find(l => l.nationalCurriculum);
                covNC = nc ? {
                    level: nc.level,
                    subjects: nc.subjects,
                    stages: [...new Set(nc.subjects.flatMap(s => s.boards))].sort(),
                } : null;
                covDefault();
                covSection.hidden = false;
                covTypeExamples();
            })
            .catch(() => { /* the section stays hidden; the page is fine without it */ });

        covInput.addEventListener('input', () => covRender(covInput.value));
    }

    /* ── Pricing billing toggle ─────────────────────────── */
    const pricingEl = document.getElementById('lp-pricing');
    const billBtns = pricingEl ? Array.from(pricingEl.querySelectorAll('.lp-bill-btn')) : [];
    if (pricingEl) pricingEl.dataset.plan = 'year';
    billBtns.forEach(btn => btn.addEventListener('click', () => {
        billBtns.forEach(b => {
            const active = b === btn;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', String(active));
        });
        pricingEl.dataset.plan = btn.dataset.interval;
    }));

    /* ── Ticker: repeat the content for a seamless loop ─── */
    // The track scrolls by exactly one copy (measured, so the gap between
    // copies is included and the wrap never jumps), and holds enough copies
    // that the ticker is still full at the end of each pass, however wide
    // the screen. Re-measured once the font has loaded (the pills change
    // width) and whenever the ticker changes size, including when the
    // landing view first becomes visible.
    const track = document.getElementById('lp-ticker-track');
    if (track) {
        const one = track.innerHTML;
        const perCopy = track.children.length;
        const PX_PER_S = 62;
        const fill = () => {
            track.innerHTML = one + one;
            const unit = track.children[perCopy].offsetLeft - track.children[0].offsetLeft;
            if (!unit) return;
            const copies = Math.ceil(track.parentElement.offsetWidth / unit) + 1;
            track.innerHTML = one.repeat(copies);
            track.style.setProperty('--lp-ticker-shift', -unit + 'px');
            track.style.animationDuration = (unit / PX_PER_S).toFixed(1) + 's';
        };
        fill();
        if (document.fonts) document.fonts.ready.then(fill);
        if ('ResizeObserver' in window) {
            let lastW = 0, resizeTimer = 0;
            new ResizeObserver(([entry]) => {
                const w = Math.round(entry.contentRect.width);
                if (w === lastW) return;
                lastW = w;
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(fill, 150);
            }).observe(track.parentElement);
        }
    }

    /* ── Generator demo loop ────────────────────────────── */
    const textEl   = document.getElementById('lp-demo-text');
    const boardEl  = document.getElementById('lp-demo-board');
    const subjEl   = document.getElementById('lp-demo-subject');
    const levelEl  = document.getElementById('lp-demo-level');
    const btnEl    = document.getElementById('lp-demo-btn');
    const titleEl  = document.getElementById('lp-mini-title');
    const demoEl   = document.getElementById('lp-demo');
    const minis    = demoEl ? Array.from(demoEl.querySelectorAll('.lp-mini')) : [];

    const topics = [
        { t: 'The French Revolution', board: 'AQA',     subject: 'History',   level: 'GCSE'    },
        { t: 'Photosynthesis',        board: 'AQA',     subject: 'Biology',   level: 'GCSE'    },
        { t: 'Macbeth: Key Themes',   board: 'Edexcel', subject: 'English',   level: 'GCSE'    },
        { t: 'Trigonometry',          board: 'OCR',     subject: 'Maths',     level: 'A-Level' },
        { t: 'The Cold War',          board: 'IB',      subject: 'History',   level: 'IB HL'   },
    ];

    async function runDemo() {
        /* eslint-disable no-constant-condition */
        while (true) {
            for (const tp of topics) {
                boardEl.textContent = tp.board;
                subjEl.textContent  = tp.subject;
                levelEl.textContent = tp.level;

                // type the topic
                for (let c = 1; c <= tp.t.length; c++) {
                    textEl.textContent = tp.t.slice(0, c);
                    await sleep(38);
                }
                await sleep(350);

                // press generate
                btnEl.classList.add('is-pressed');
                await sleep(200);
                btnEl.classList.remove('is-pressed');

                // slides pop in
                titleEl.textContent = tp.t;
                await sleep(150);
                for (const m of minis) {
                    m.classList.add('is-in');
                    await sleep(150);
                }
                await sleep(2400);

                // clear for the next topic
                minis.forEach(m => m.classList.remove('is-in'));
                for (let c = tp.t.length; c >= 0; c--) {
                    textEl.textContent = tp.t.slice(0, c);
                    await sleep(10);
                }
                await sleep(300);
            }
        }
    }

    if (demoEl && textEl && btnEl) {
        if (reduceMotion) {
            // static frame: first topic, slides shown
            minis.forEach(m => m.classList.add('is-in'));
        } else if ('IntersectionObserver' in window) {
            // start the loop the first time the demo is on screen
            const demoIO = new IntersectionObserver(entries => {
                entries.forEach(e => {
                    if (e.isIntersecting) {
                        demoIO.unobserve(demoEl);
                        textEl.textContent = '';
                        runDemo();
                    }
                });
            }, { threshold: 0.3 });
            demoIO.observe(demoEl);
        } else {
            textEl.textContent = '';
            runDemo();
        }
    }
})();
